import { useCallback, useLayoutEffect, useState } from 'react'
import type { TextareaHTMLAttributes } from 'react'

/**
 * Un área de texto que se estira con lo que tiene dentro, en vez de sacar
 * barra de desplazamiento.
 *
 * El caso que lo pidió: la justificación de la necesidad de una vacante. Son
 * párrafos que alguien más va a leer para decidir, y leerlos por una ventana
 * de cuatro renglones obliga a desplazar dentro de una pantalla que ya se
 * desplaza. Creciendo, el texto se lee completo de un vistazo.
 *
 * `filasMinimas` fija el alto de partida: aunque esté vacía la caja se ve, y
 * nunca se encoge más allá de eso. Hacia arriba no hay tope a propósito —
 * ponerlo devolvería la barra, que es justo lo que se quitó.
 */
export const TextareaQueCrece = ({
  filasMinimas = 3,
  className = '',
  ...resto
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  filasMinimas?: number
}) => {
  /**
   * Ref de callback y no `useRef`: así el ajuste corre en cuanto el nodo
   * existe. Con `useRef` el efecto puede correr antes —pasó ya con los
   * observadores de las tarjetas— y mediría contra un nodo que no está.
   */
  const [nodo, setNodo] = useState<HTMLTextAreaElement | null>(null)

  const ajustar = useCallback(() => {
    if (!nodo) return
    // Soltar el alto primero: sin esto `scrollHeight` nunca baja del alto que
    // ya tenía puesto, y la caja crecería pero jamás se encogería al borrar.
    nodo.style.height = 'auto'
    nodo.style.height = `${nodo.scrollHeight}px`
  }, [nodo])

  /**
   * Sin lista de dependencias a propósito: hay que volver a medir en cada
   * pintada, no solo cuando cambia el texto.
   *
   * Lo pidió la ficha de la solicitud. Pasar de consulta a edición no toca el
   * valor, pero devuelve el borde y el relleno que la vista de consulta
   * aplana y sube el tipo de 14px a 16px: el mismo texto pasa a necesitar un
   * 45% más de alto. Midiendo solo al cambiar el valor, la caja se quedaba
   * con la medida de consulta y recortaba el resto — y como aquí no hay
   * barra, ese texto no se alcanzaba de ninguna forma.
   *
   * Medir de más es barato: son dos escrituras de estilo y la caja casi
   * siempre ya está en su alto.
   */
  useLayoutEffect(() => {
    ajustar()
  })

  useLayoutEffect(() => {
    if (!nodo) return

    /**
     * Al angostarse la caja el texto reparte en más renglones y el alto que
     * calculamos se queda corto: volvería a aparecer la barra. Se compara el
     * ancho y no se reacciona a cualquier cambio porque el alto lo movemos
     * nosotros, y atenderlo sería un bucle.
     */
    let anchoPrevio = nodo.clientWidth
    const observador = new ResizeObserver(() => {
      if (nodo.clientWidth === anchoPrevio) return
      anchoPrevio = nodo.clientWidth
      ajustar()
    })

    observador.observe(nodo)
    return () => observador.disconnect()
  }, [nodo, ajustar])

  return (
    <textarea
      ref={setNodo}
      rows={filasMinimas}
      // `resize-none` porque el alto ya lo lleva el contenido: dejar la
      // esquinita sería ofrecer un ajuste que el siguiente tecleo deshace.
      className={`resize-none overflow-hidden ${className}`}
      {...resto}
    />
  )
}
